/**
 * @callback GenEventResponderCleanup
 * @property {Function} cleanupEvent - A method that will clean up any listening events created by this responder method call
 */

/**
 * Returns a callback for responding to a CustomEvent in a React component
 *
 * @callback GenEventResponder
 * @param {Function} callback - A function that will be called with the custom event as the only argument if the signature matches
 * @param {Object} [signature] - An optional signature object that will be compared to the event.detail, based on the `signatureParams` passed to the generator
 * @param {Array} [dependencies] - An array of values that will be passed to useEffect. Useful for referencing state variables in the responding component.
 * @returns {GenEventResponderCleanup} cleanup
 */

/**
 *
 * @param {GenEventHandler|React.useEffect} eventHandler
 * @param {String} eventName - The name for the CustomEvent
 * @param {Array} signatureParams - Keys that will be used to compare the event details in the responder to determine if the CustomEvent should be responded to
 * @returns {GenEventResponder}
 */
const createEventResponder = ({ eventHandler, eventName, signatureParams }) => (
  callback = () => {},
  { signature = {}, dependencies = [] } = {}
) => {
  const handler = e => {
    if (signatureParams.every(param => e.detail[param] === signature[param])) {
      callback(e);
    }
  };
  let cleanupEvent = () => null;

  const event = () => {
    global.addEventListener(eventName, handler);

    cleanupEvent = () => {
      global.removeEventListener(eventName, handler);
    };

    return cleanupEvent;
  };

  const response = eventHandler(event, dependencies);
  const cleanup = () => cleanupEvent();

  if (!response) {
    return {
      cleanupEvent: cleanup
    };
  }

  response.cleanupEvent = cleanup;

  return response;
};

export default createEventResponder;
